import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import { get, isBoolean, isNumber } from "lodash";
import PropTypes from "prop-types";

import Collapse from "./Collapse";

const mapBooleanToYesNo = (value) => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return "-";
};

const FireProtectionView = ({
  fireProtection,
  isExpandable = true,
  t,
  isTracking = false,
}) => {
  const {
    isFirstAidHoseReelProvided,
    isWetControllerSystemProvided,
    isInternalHydrantProvided,
    isBasementHasSprinklerSystem,
    isYardHydrantProvided,
    isFloorHasSprinklerSystem,
    isGroundFloorHasSprinklerSystem,
    isHollowPlinthHasSprinklerSystem,
    isPublicAddressingSystem,
    underGroundTankCapacity,
    overHeadTankCapacity,
    firePumpLocation,
    numberOfFirstAidHoseReel,
    numberOfWetControllerSystem,
    numberOfInternalHydrant,
    numberOfYardHydrant,
    specialFireProtectionSystemProvided,
    isEvacuationPlan,
    isPublicAddressingSystemInTenantArea,
    isExternalLifts,
    groundFloorSprinklerSystemQuantity,
    hollowPlinthSprinklerSystemQuantity,
  } = fireProtection;

  const fields = useMemo(
    () => [
      {
        name: "UnderGround Tank Capacity",
        key: "underGroundTankCapacity",
        isRequired: true,
        isVisible: isTracking ? isNumber(underGroundTankCapacity) : true,
      },
      {
        name: "OverHead Tank Capacity",
        key: "overHeadTankCapacity",
        isRequired: true,
        isVisible: isTracking ? isNumber(overHeadTankCapacity) : true,
      },
      {
        name: "Location of Fire Pump House",
        key: "firePumpLocation",
        isRequired: true,
        isVisible: isTracking ? !!firePumpLocation : true,
      },
      {
        name: "Has first aid hose reel been provided?",
        key: "isFirstAidHoseReelProvided",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isFirstAidHoseReelProvided) : true,
      },
      {
        name: "Number of First Aid Hose Reel",
        key: "numberOfFirstAidHoseReel",
        isRequired: true,
        isVisible: isTracking
          ? !!numberOfFirstAidHoseReel
          : isFirstAidHoseReelProvided,
      },
      {
        name: "Has the wet riser/ downcomer system, been provided?",
        key: "isWetControllerSystemProvided",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isWetControllerSystemProvided) : true,
      },
      {
        name: "Number of Risers",
        key: "numberOfWetControllerSystem",
        isRequired: true,
        isVisible: isTracking
          ? !!numberOfWetControllerSystem
          : isWetControllerSystemProvided,
      },
      {
        name: "Have internal hydrants been provided?",
        key: "isInternalHydrantProvided",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isInternalHydrantProvided) : true,
      },
      {
        name: "Number of Internal Hydrants",
        key: "numberOfInternalHydrant",
        isRequired: true,
        isVisible: isTracking
          ? !!numberOfInternalHydrant
          : isInternalHydrantProvided,
      },
      {
        name: "Have yard hydrants been provided?",
        key: "isYardHydrantProvided",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isYardHydrantProvided) : true,
      },
      {
        name: "Number of Yard Hydrants",
        key: "numberOfYardHydrant",
        isRequired: true,
        isVisible: isTracking ? !!numberOfYardHydrant : isYardHydrantProvided,
      },

      {
        name: "Special Fire Protection System",
        key: "specialFireProtectionSystemProvided",
        isRequired: false,
        isVisible: isTracking ? !!specialFireProtectionSystemProvided : true,
      },
      {
        name: "Evacuation plan",
        key: "isEvacuationPlan",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isEvacuationPlan) : true,
      },
      {
        name: "Public addressing system",
        key: "isPublicAddressingSystem",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isPublicAddressingSystem) : true,
      },
      {
        name: "Public addressing system provided in tenant area",
        key: "isPublicAddressingSystemInTenantArea",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking
          ? isBoolean(isPublicAddressingSystemInTenantArea)
          : isPublicAddressingSystem,
      },
      {
        name: "External Lifts",
        key: "isExternalLifts",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isExternalLifts) : true,
      },
      {
        name: "Ground Floor",
        key: "isGroundFloorHasSprinklerSystem",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking
          ? isBoolean(isGroundFloorHasSprinklerSystem)
          : true,
      },
      {
        name: "Ground Floor Quantity",
        key: "groundFloorSprinklerSystemQuantity",
        isRequired: true,
        isVisible: isTracking
          ? !!groundFloorSprinklerSystemQuantity
          : isGroundFloorHasSprinklerSystem,
      },
      {
        name: "Hollow Plinth",
        key: "isHollowPlinthHasSprinklerSystem",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking
          ? isBoolean(isHollowPlinthHasSprinklerSystem)
          : true,
      },
      {
        name: "Hollow Plinth Quantity",
        key: "hollowPlinthSprinklerSystemQuantity",
        isRequired: true,
        isVisible: isTracking
          ? !!hollowPlinthSprinklerSystemQuantity
          : isHollowPlinthHasSprinklerSystem,
      },
      {
        name: "Basements",
        key: "isBasementHasSprinklerSystem",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isBasementHasSprinklerSystem) : true,
      },
      {
        name: "Floors",
        key: "isFloorHasSprinklerSystem",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isFloorHasSprinklerSystem) : true,
      },
    ],
    [
      isFirstAidHoseReelProvided,
      isWetControllerSystemProvided,
      isInternalHydrantProvided,
      isBasementHasSprinklerSystem,
      isYardHydrantProvided,
      isGroundFloorHasSprinklerSystem,
      isFloorHasSprinklerSystem,
      isHollowPlinthHasSprinklerSystem,
      isPublicAddressingSystem,
      underGroundTankCapacity,
      overHeadTankCapacity,
      firePumpLocation,
      numberOfFirstAidHoseReel,
      numberOfWetControllerSystem,
      numberOfInternalHydrant,
      numberOfYardHydrant,
      specialFireProtectionSystemProvided,
      isEvacuationPlan,
      isPublicAddressingSystemInTenantArea,
      isExternalLifts,
      groundFloorSprinklerSystemQuantity,
      hollowPlinthSprinklerSystemQuantity,
    ]
  );

  const isCollapseVisible = useMemo(
    () => fields.some(({ isVisible }) => isVisible),
    [fields]
  );

  return (
    <>
      {isCollapseVisible && (
        <Collapse title="Fire Protection" isExpandable={isExpandable}>
          <Row className="d-flex mb-2">
            {fields.map(({ name, key, isVisible, isRequired, renderField }) => {
              const value = get(fireProtection, key, "");
              if (typeof isVisible !== "boolean" || isVisible) {
                return (
                  <Col key={key} lg={6} className="mb-1">
                    <Row>
                      <Col md={5}>{t(name)}:</Col>
                      <Col
                        md={7}
                        className="fw-semibold"
                        style={{ wordBreak: "break-word" }}
                      >
                        {renderField
                          ? renderField(value)
                          : get(fireProtection, key, null) ??
                            (isRequired ? "-" : "N/A")}
                      </Col>
                    </Row>
                  </Col>
                );
              }
            })}
          </Row>
        </Collapse>
      )}
    </>
  );
};

FireProtectionView.propTypes = {
  fireProtection: PropTypes.object,
  isExpandable: PropTypes.bool,
  isTracking: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(FireProtectionView);
